/* File: src/components/friends/FriendsPage.js */

/* src/components/friends/FriendsPage.js */
import React, { useState, useEffect, useCallback } from 'react';
import { useProfile } from '../../contexts/ProfileContext';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../ui/ConfirmationModal'; // For remove friend confirmation
import FriendLeaderboard from './FriendLeaderboard'; // Import the leaderboard component
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; // Import for potential future icons if needed
import { faPencil } from '@fortawesome/free-solid-svg-icons'; // Import pencil icon for FriendList

// --- Reusable Loading Spinner ---
const InlineSpinner = () => <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>;
const LoadingSpinner = () => ( <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-indigo-500 mx-auto my-4"></div> ); // Centered spinner for sections

// --- Search Component (Defined In-File) ---
const FriendSearch = ({ onSendRequest }) => {
    const { searchUsers } = useProfile();
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [requestStatus, setRequestStatus] = useState({}); // Key: profileId, Value: status

    // Debounced search handler
    const handleSearch = useCallback(async (term) => {
        if (term.trim().length < 1) {
            setResults([]); setError(''); return;
        }
        setIsLoading(true); setError('');
        try {
            const users = await searchUsers(term.trim());
            setResults(users || []); // Ensure users is an array
            // Reset status only for users currently in results
            setRequestStatus(prev => {
                const newStatus = {};
                (users || []).forEach(u => {
                    newStatus[u.profileId] = prev[u.profileId] === 'sent' ? 'sent' : 'idle';
                });
                return newStatus;
            });
        } catch (err) {
            console.error("Search Error:", err);
            setError(err.message || 'Failed to search users.');
            setResults([]);
        } finally {
            setIsLoading(false);
        }
    }, [searchUsers]); // Dependency: searchUsers action

    // Effect for debouncing search input
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            handleSearch(searchTerm);
        }, 300); // 300ms delay
        return () => clearTimeout(delayDebounceFn); // Cleanup timer
    }, [searchTerm, handleSearch]); // Dependencies: searchTerm and the debounced handler

    // Handler for sending a friend request
    const handleSend = async (targetUser) => {
        const currentStatus = requestStatus[targetUser.profileId] || 'idle';
        if (currentStatus === 'sending' || currentStatus === 'sent') return; // Prevent duplicate sends

        setRequestStatus(prev => ({ ...prev, [targetUser.profileId]: 'sending' }));
        setError(''); // Clear previous errors

        try {
            // Call the context action passed as a prop
            const result = await onSendRequest(targetUser.username);
            // Update status based on success/failure from the context action
            setRequestStatus(prev => ({
                ...prev,
                [targetUser.profileId]: result?.success ? 'sent' : 'error'
            }));
             if (!result?.success) {
                 // Display the specific error message from the backend if available
                 setError(result?.message || `Failed to send request.`);
             }
        } catch (err) {
            // Catch errors from the onSendRequest call itself (e.g., network issues)
            console.error("Error sending friend request:", err);
            setError(err.message || `Error sending request.`);
            setRequestStatus(prev => ({ ...prev, [targetUser.profileId]: 'error' }));
        }
    };

    // Render the search component UI
    return (
        <div className="mb-6 p-4 border rounded-lg bg-gray-50 shadow-sm">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Add Friend</h3>
            <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by username..."
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                aria-label="Search for friends by username"
            />
            {/* Display search errors */}
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
            {/* Search results area with scrolling */}
            <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
                {isLoading && <LoadingSpinner />}
                {/* Message if no users found */}
                {!isLoading && results.length === 0 && searchTerm.trim().length > 0 && (
                    <p className="text-xs text-gray-500 italic px-2">No users found matching "{searchTerm}".</p>
                )}
                {/* List search results */}
                {results.map(user => {
                    const status = requestStatus[user.profileId] || 'idle';
                    const isDisabled = status === 'sending' || status === 'sent';
                    let buttonText = 'Add'; // Shortened text
                    let buttonClass = 'bg-blue-500 text-white hover:bg-blue-600';
                    // Determine button state based on request status
                    if (status === 'sending') { buttonText = '...'; buttonClass = 'bg-gray-400 text-gray-800 cursor-wait'; }
                    else if (status === 'sent') { buttonText = 'Sent ✓'; buttonClass = 'bg-green-200 text-green-800 cursor-default'; }
                    else if (status === 'error') { buttonText = 'Retry'; buttonClass = 'bg-red-500 text-white hover:bg-red-600'; }

                    return (
                        <div key={user.profileId} className="flex justify-between items-center p-2 bg-white border rounded">
                            <span className="text-sm text-gray-800 truncate mr-2" title={user.username}>{user.username}</span>
                            <button
                                onClick={() => handleSend(user)}
                                disabled={isDisabled && status !== 'error'} // Allow retry on error
                                className={`px-3 py-1 text-xs rounded transition-colors flex-shrink-0 ${buttonClass} disabled:opacity-70 min-w-[60px] text-center`} // Min width for consistency
                                aria-label={`Send friend request to ${user.username}`}
                            >
                                {status === 'sending' && <InlineSpinner />}
                                {buttonText}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}; // --- End FriendSearch Component ---

// --- List Components (Defined In-File) ---

// --- FriendList Component (Modified) ---
const FriendList = ({ friends, onRemove, actionStatus, setActionStatus, isEditing, setIsEditing }) => {
    const handleRemoveClick = (id, name) => {
        // No need to set actionStatus here as the button click triggers the modal,
        // and the modal confirmation handles the 'loading' state.
        onRemove(id, name); // Trigger confirmation modal in parent
    };

    return (
        <div className="mb-6 p-4 border rounded-lg bg-white shadow-sm">
            <div className="flex justify-between items-center mb-3"> {/* Container for title and icon */}
                <h3 className="text-lg font-semibold text-gray-700">Friends ({friends.length})</h3>
                {/* Show pencil icon only if there are friends */}
                {friends.length > 0 && (
                    <button
                        onClick={() => setIsEditing(!isEditing)}
                        // *** MODIFIED LINE BELOW ***
                        className={`w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 transition-colors ${
                            isEditing ? 'bg-indigo-100 text-indigo-600' : '' // Optional: Highlight when active
                        }`}
                        title={isEditing ? "Done Editing" : "Edit Friends List"}
                        aria-label={isEditing ? "Done Editing Friends List" : "Edit Friends List"}
                    >
                        {/* Icon size remains h-4 w-4 */}
                        <FontAwesomeIcon icon={faPencil} className="h-4 w-4" />
                    </button>
                )}
            </div>
            {friends.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No friends yet. Use the search above!</p>
            ) : (
                <ul className="space-y-2">
                    {friends.map(friend => {
                         const statusKey = `remove_${friend.id}`;
                         const status = actionStatus[statusKey] || 'idle';
                         const isProcessing = status === 'loading'; // Only disable button during actual removal API call

                        return (
                            <li key={friend.id} className="flex justify-between items-center p-2 bg-gray-50 border rounded">
                                <span className="text-sm text-gray-800 truncate mr-2" title={friend.friendUsername}>{friend.friendUsername}</span>
                                {/* Conditionally render the remove button based on isEditing */}
                                {isEditing && (
                                    <button
                                        onClick={() => handleRemoveClick(friend.id, friend.friendUsername)}
                                        disabled={isProcessing} // Disable only when API call is in progress
                                        className="text-xs text-red-500 hover:text-red-700 hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-wait flex-shrink-0 px-2 py-1"
                                        aria-label={`Remove friend ${friend.friendUsername}`}
                                    >
                                        {/* Text changes based on isProcessing (API call state), not confirming state */}
                                        {isProcessing ? '...' : 'Remove'}
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}; // --- End FriendList Component ---


// --- RequestList Component ---
const RequestList = ({ title, requests, onAccept, onHandle, isReceived, actionStatus, setActionStatus }) => {
    const acceptText = 'Accept';
    const rejectText = 'Reject';
    const cancelText = 'Cancel';

    // Handler for primary actions (Accept/Cancel)
    const handlePrimaryAction = async (requestId) => {
        const actionKey = `${isReceived ? 'accept' : 'cancel'}_${requestId}`;
        setActionStatus(prev => ({ ...prev, [actionKey]: 'loading' }));
        try {
            // Call appropriate context action based on list type
            if (isReceived && onAccept) await onAccept(requestId);
            else if (!isReceived && onHandle) await onHandle(requestId); // onHandle is Cancel for sent
            // Success - Listener will update the list. Clean up status after a delay.
            setTimeout(() => setActionStatus(prev => { const ns = { ...prev }; delete ns[actionKey]; return ns; }), 500);
        } catch (error) {
            console.error(`Error during ${actionKey}:`, error);
            setActionStatus(prev => ({ ...prev, [actionKey]: 'error' })); // Set error status
            // Clear error status after a delay
            setTimeout(() => setActionStatus(prev => { const ns = { ...prev }; delete ns[actionKey]; return ns; }), 2000);
        }
    };

    // Handler for secondary action (Reject)
    const handleSecondaryAction = async (requestId) => {
        const actionKey = `reject_${requestId}`;
        setActionStatus(prev => ({ ...prev, [actionKey]: 'loading' }));
        try {
             // onHandle is Reject for received requests
             if (isReceived && onHandle) await onHandle(requestId);
             // Success - Listener updates list. Clean up status.
             setTimeout(() => setActionStatus(prev => { const ns = { ...prev }; delete ns[actionKey]; return ns; }), 500);
        } catch (error) {
             console.error(`Error during ${actionKey}:`, error);
             setActionStatus(prev => ({ ...prev, [actionKey]: 'error' }));
             setTimeout(() => setActionStatus(prev => { const ns = { ...prev }; delete ns[actionKey]; return ns; }), 2000);
        }
    };

    // Render the list of requests
    return (
        <div className="mb-6 p-4 border rounded-lg bg-gray-50 shadow-sm">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">{title} ({requests.length})</h3>
            {requests.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No {title.toLowerCase().replace('pending ', '')} found.</p>
            ) : (
                <ul className="space-y-2">
                    {requests.map(req => {
                        // Determine action statuses and username for the current request item
                        const acceptKey = `accept_${req.id}`; const cancelKey = `cancel_${req.id}`; const rejectKey = `reject_${req.id}`;
                        const acceptStatus = actionStatus[acceptKey] || 'idle'; const cancelStatus = actionStatus[cancelKey] || 'idle'; const rejectStatus = actionStatus[rejectKey] || 'idle';
                        const isProcessing = acceptStatus === 'loading' || cancelStatus === 'loading' || rejectStatus === 'loading';
                        const username = isReceived ? req.requestingUsername : req.recipientUsername;

                        return (
                            <li key={req.id} className="flex justify-between items-center p-2 bg-white border rounded">
                                <span className="text-sm text-gray-800 truncate mr-2" title={username}>{username}</span>
                                {/* Button container */}
                                <div className="flex space-x-2 flex-shrink-0">
                                    {/* Conditional rendering of buttons based on list type */}
                                    {isReceived && (
                                        <>
                                        <button onClick={() => handlePrimaryAction(req.id)} disabled={isProcessing} className="text-xs text-green-600 hover:text-green-800 hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-wait px-2 py-1" aria-label={`Accept request from ${username}`}> {acceptStatus === 'loading' ? '...' : acceptStatus === 'error' ? 'Error' : acceptText} </button>
                                        <button onClick={() => handleSecondaryAction(req.id)} disabled={isProcessing} className="text-xs text-red-500 hover:text-red-700 hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-wait px-2 py-1" aria-label={`Reject request from ${username}`}> {rejectStatus === 'loading' ? '...' : rejectStatus === 'error' ? 'Error' : rejectText} </button>
                                        </>
                                    )}
                                    {!isReceived && (
                                         <button onClick={() => handlePrimaryAction(req.id)} disabled={isProcessing} className="text-xs text-red-500 hover:text-red-700 hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-wait px-2 py-1" aria-label={`Cancel request sent to ${username}`}> {cancelStatus === 'loading' ? '...' : cancelStatus === 'error' ? 'Error' : cancelText} </button>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}; // --- End RequestList Component ---


// --- Main FriendsPage Component (Modified) ---
const FriendsPage = () => {
    // Get data and actions from context
    const { activeProfile, isLoading, error: profileError, sendFriendRequest, acceptFriendRequest, rejectFriendRequest, cancelFriendRequest, removeFriend } = useProfile();
    const navigate = useNavigate();
    // Local state for UI interactions
    const [actionStatus, setActionStatus] = useState({}); // Tracks loading/error states for buttons
    const [friendToRemove, setFriendToRemove] = useState({ id: null, name: '' }); // Data for confirmation modal
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false); // Modal visibility
    const [isRemoving, setIsRemoving] = useState(false); // Modal specific loading state
    const [isEditingFriends, setIsEditingFriends] = useState(false); // <<<--- ADDED state for edit mode

    // Callback to open the remove confirmation modal
    const handleRemoveRequest = useCallback((id, name) => {
        // We don't set actionStatus here, just prepare modal data
        setFriendToRemove({ id, name }); // Store details for modal
        setShowRemoveConfirm(true); // Show modal
    }, []); // No dependencies needed

    // Callback to execute friend removal after confirmation
    const handleConfirmRemove = useCallback(async () => {
        if (!friendToRemove.id) return; // Guard clause
        const statusKey = `remove_${friendToRemove.id}`;
        setIsRemoving(true); // Set modal loading state
        setActionStatus(prev => ({ ...prev, [statusKey]: 'loading' })); // Set item loading state (for button disabling in FriendList)
        try {
            await removeFriend(friendToRemove.id); // Call context action
            // Success: Listener handles UI update. State cleanup happens in finally.
        } catch (error) {
            console.error("Error removing friend:", error);
            setActionStatus(prev => ({ ...prev, [statusKey]: 'error' })); // Set item error state
            // Optional: Clear error state after a delay - kept for consistency, though button is hidden now
            setTimeout(() => setActionStatus(prev => { const ns = { ...prev }; delete ns[statusKey]; return ns; }), 2000);
        } finally {
            // Reset loading states and modal visibility
            setIsRemoving(false);
            setShowRemoveConfirm(false);
            setIsEditingFriends(false); // <<<--- Exit edit mode after confirmation
            // Clean up action status unless it was set to 'error'
            setActionStatus(prev => { if (prev[statusKey] !== 'error') { const ns = { ...prev }; delete ns[statusKey]; return ns; } return prev; });
            setFriendToRemove({ id: null, name: '' }); // Clear friend details
        }
    }, [friendToRemove.id, removeFriend]); // Dependencies

    // Callback to cancel the removal process
    const handleCancelRemove = useCallback(() => {
        setShowRemoveConfirm(false); // Hide modal
        setIsEditingFriends(false); // <<<--- Exit edit mode on cancel
        // Clear any potentially lingering 'loading' or 'error' status for the item being considered
        if (friendToRemove.id) { setActionStatus(prev => { const ns = { ...prev }; delete ns[`remove_${friendToRemove.id}`]; return ns; }); }
        setFriendToRemove({ id: null, name: '' }); // Clear friend details
    }, [friendToRemove.id]); // Dependency

    // --- Render Logic ---

    // Loading state from context
    if (isLoading && !activeProfile) {
        return <div className="p-6 text-center text-gray-500">Loading friend data...</div>;
    }

    // No active profile state
    if (!activeProfile) {
        return (
            <div className="p-6 text-center text-gray-500">
                No active profile selected.
                <button onClick={() => navigate('/profile-selection')} className="ml-2 text-blue-600 hover:underline">
                    Select Profile
                </button>
            </div>
        );
    }

    // Extract lists safely, defaulting to empty arrays
    const friends = activeProfile.friendsList || [];
    const received = activeProfile.friendRequestsReceived || [];
    const sent = activeProfile.friendRequestsSent || [];

    return (
        <div className="p-4 sm:p-6 bg-gray-100 min-h-screen">
            {/* Container with responsive max-width */}
            <div className="max-w-7xl mx-auto relative"> {/* Increased max-width slightly */}
                {/* Responsive Page Header */}
                <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2 sm:mb-0">
                        Manage Friends & Leaderboard
                    </h1>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded px-1 self-start sm:self-center"
                    >
                        ← Back to Dashboard
                    </button>
                </div>

                {/* Global Error Display */}
                {profileError && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded text-sm">
                        Error: {profileError}
                    </div>
                )}

                {/* Responsive Grid Layout - Adjusted columns and spans */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

                    {/* Leaderboard Column (spans 3 cols on large screens) */}
                    <div className="lg:col-span-3">
                        <FriendLeaderboard />
                    </div>

                    {/* Friend Management Column (spans 1 col on large screens) */}
                    <div className="space-y-6 lg:col-span-1">
                        <FriendSearch onSendRequest={sendFriendRequest} />
                        <RequestList title="Received Requests" requests={received} onAccept={acceptFriendRequest} onHandle={rejectFriendRequest} isReceived={true} actionStatus={actionStatus} setActionStatus={setActionStatus} />
                        <RequestList title="Sent Requests" requests={sent} onHandle={cancelFriendRequest} isReceived={false} actionStatus={actionStatus} setActionStatus={setActionStatus} />
                        {/* Pass editing state and setter to FriendList */}
                        <FriendList
                            friends={friends}
                            onRemove={handleRemoveRequest}
                            actionStatus={actionStatus}
                            setActionStatus={setActionStatus}
                            isEditing={isEditingFriends}
                            setIsEditing={setIsEditingFriends}
                        />
                    </div>

                </div> {/* End Grid */}

                {/* Confirmation Modal */}
                <ConfirmationModal
                    isOpen={showRemoveConfirm}
                    title="Remove Friend"
                    // Use children prop for message content
                    message={null} // Ensure old prop is not used if present
                    onConfirm={handleConfirmRemove}
                    // Pass onClose={handleCancelRemove} for the cancel action
                    onClose={handleCancelRemove}
                    confirmText={isRemoving ? "Removing..." : "Remove"}
                    isDestructive={true}
                    // Keep cancelText default or customize if needed
                    cancelText="Cancel"
                >
                    {/* Pass the message as children */}
                    <p>Are you sure you want to remove <span className="font-semibold">{friendToRemove.name}</span>?</p>
                    <p className="text-xs text-gray-500 mt-1">This action cannot be undone.</p>
                </ConfirmationModal>

            </div> {/* End Container */}
        </div> // End Page Wrapper
    );
};

export default FriendsPage;